import React from 'react';
import { Link } from 'react-router-dom';

// Static privacy policy page hosted at /privacy. Referenced from the App
// Store Connect listing's required Privacy Policy URL field and from the
// in-app footer / settings page.
//
// This is the canonical policy text — when something material changes (new
// data type collected, new third-party processor, etc.) update both the
// content and the "Last updated" date below. Apple reviews the linked page
// at App Store submission time, so keep claims consistent with the App
// Privacy questionnaire in App Store Connect.

const LAST_UPDATED = 'May 11, 2026';

const PrivacyPage: React.FC = () => {
  return (
    <div className="min-h-screen bg-surface-0 px-4 py-10 sm:py-16">
      <div className="max-w-2xl mx-auto">
        <div className="mb-6">
          <Link
            to="/"
            className="text-body-sm text-ink-subtle hover:text-ink underline-offset-2 hover:underline"
          >
            ← Back to Flowspect
          </Link>
        </div>

        <header className="mb-8">
          <h1 className="font-display text-display-sm text-ink mb-2">Privacy Policy</h1>
          <p className="text-body-sm text-ink-muted">Last updated {LAST_UPDATED}</p>
        </header>

        <article className="prose prose-stone max-w-none text-ink-muted space-y-6">
          <section>
            <h2 className="text-heading-md text-ink mb-2">Overview</h2>
            <p>
              Flowspect is a home-inspection report-writing tool used by
              professional inspectors and shared with their clients (buyers,
              sellers, and their agents). This policy explains what
              information we collect, why we collect it, and who has access
              to it.
            </p>
            <p>
              We do not sell data, we do not run advertising, and we do not
              use third-party analytics or tracking SDKs. Everything we
              collect is used only to make the product work.
            </p>
          </section>

          <section>
            <h2 className="text-heading-md text-ink mb-2">Information we collect</h2>

            <h3 className="text-heading-sm text-ink mt-4 mb-1">From inspectors (account holders)</h3>
            <ul className="list-disc pl-6 space-y-1">
              <li>Account details from your sign-in provider (Clerk): your name, email address, and a unique account identifier. Phone number, company name, license number, and business address if you fill them in on your profile.</li>
              <li>Logo and profile photo if you upload them.</li>
              <li>Inspection content you create: section structure, component attributes, findings, photos, voice memo transcripts, signed agreements, scheduling availability, and report metadata.</li>
              <li>Payment instructions you enter (we do not process payments inside the app; payment links you add point to your own payment provider).</li>
            </ul>

            <h3 className="text-heading-sm text-ink mt-4 mb-1">From your clients</h3>
            <ul className="list-disc pl-6 space-y-1">
              <li>Name, email, optional phone, and property address — entered by you or submitted through your public booking page.</li>
              <li>Signature image and timestamp when a client signs a pre-inspection agreement.</li>
              <li>Repair-request items a client submits from a published report (which findings, requested action, estimated cost).</li>
              <li>The IP address used to sign an agreement, for non-repudiation.</li>
            </ul>

            <h3 className="text-heading-sm text-ink mt-4 mb-1">From the device</h3>
            <ul className="list-disc pl-6 space-y-1">
              <li>Photos and videos you capture or select for inspections — uploaded to encrypted object storage and attached to the inspection.</li>
              <li>Audio captured for voice memos is transcribed on the device and the resulting transcript is sent to our AI processor (see below). The raw audio is not retained.</li>
              <li>Network status (online / offline) so the app can queue work while you're out of signal.</li>
            </ul>
          </section>

          <section>
            <h2 className="text-heading-md text-ink mb-2">What we do not collect</h2>
            <ul className="list-disc pl-6 space-y-1">
              <li>Device location / GPS — property addresses are entered by hand, not captured from your device.</li>
              <li>Phone contacts.</li>
              <li>Health, fitness, or financial-account data.</li>
              <li>Browsing history outside the app.</li>
              <li>Cross-app or cross-website tracking identifiers.</li>
            </ul>
          </section>

          <section>
            <h2 className="text-heading-md text-ink mb-2">How we use the information</h2>
            <ul className="list-disc pl-6 space-y-1">
              <li><strong>Operate the product</strong> — sign you in, save your inspections, generate reports, send agreements and reports by email, render the client portal.</li>
              <li><strong>AI features you opt into</strong> — when you take a photo and request AI vision analysis, the photo is sent to Anthropic's Claude API for component identification + defect detection. When you record a voice memo, the transcript is sent to Claude to turn it into draft findings. Anthropic processes these requests under their API terms and does not train on the data.</li>
              <li><strong>Transactional email</strong> — agreement signing links, signed-agreement PDFs, report delivery, and booking confirmations are sent through Resend on our behalf.</li>
              <li><strong>Storage</strong> — inspection photos are stored in Cloudflare R2 (S3-compatible object storage). The rest of the data is in our PostgreSQL database hosted on Neon.</li>
            </ul>
          </section>

          <section>
            <h2 className="text-heading-md text-ink mb-2">Third-party processors</h2>
            <p>We use the following providers to operate Flowspect. Each is bound by a Data Processing Agreement and uses your data only to deliver their part of the service.</p>
            <ul className="list-disc pl-6 space-y-1">
              <li><strong>Clerk</strong> — account sign-in, identity management.</li>
              <li><strong>Neon</strong> — managed PostgreSQL database.</li>
              <li><strong>Cloudflare R2</strong> — object storage for inspection photos and signed agreement PDFs.</li>
              <li><strong>Anthropic (Claude)</strong> — AI vision analysis on photos you submit and voice-memo transcript analysis. Anthropic does not train on API submissions.</li>
              <li><strong>Resend</strong> — transactional email delivery.</li>
            </ul>
          </section>

          <section>
            <h2 className="text-heading-md text-ink mb-2">Sharing</h2>
            <p>
              We share inspection content with the recipients you direct us
              to: typically your client and (optionally) the buyer's agent
              when you publish a report or send an agreement. Reports are
              accessible at a long, hard-to-guess public URL. We do not share
              your data with anyone else, and we do not sell it.
            </p>
          </section>

          <section>
            <h2 className="text-heading-md text-ink mb-2">Retention and deletion</h2>
            <p>
              We keep inspection data for as long as your account is active.
              You can delete individual inspections, photos, and findings
              from inside the app. To delete your entire account and all
              data attached to it, email{' '}
              <a href="mailto:privacy@flowspect.app" className="text-brand underline">
                privacy@flowspect.app
              </a>{' '}
              and we'll process the request within 30 days.
            </p>
          </section>

          <section>
            <h2 className="text-heading-md text-ink mb-2">Security</h2>
            <p>
              Data is transmitted over HTTPS, stored encrypted at rest by our
              providers, and access is gated by your sign-in credentials.
              Public report links and agreement signing links use long
              randomly-generated tokens so they're not guessable.
            </p>
          </section>

          <section>
            <h2 className="text-heading-md text-ink mb-2">Children</h2>
            <p>
              Flowspect is a B2B tool for licensed home inspectors. It's not
              directed at children under 13 and we do not knowingly collect
              data from them.
            </p>
          </section>

          <section>
            <h2 className="text-heading-md text-ink mb-2">Changes to this policy</h2>
            <p>
              If we change how we handle data, we'll update this page and the
              "Last updated" date above. Material changes will be announced
              by email to account holders.
            </p>
          </section>

          <section>
            <h2 className="text-heading-md text-ink mb-2">Contact</h2>
            <p>
              Questions, data-access requests, or deletion requests:{' '}
              <a href="mailto:privacy@flowspect.app" className="text-brand underline">
                privacy@flowspect.app
              </a>
              .
            </p>
          </section>
        </article>
      </div>
    </div>
  );
};

export default PrivacyPage;
